import React from 'react';
import { motion } from 'framer-motion';
import { Database, Copy, CopySimple, ArrowsLeftRight } from '@phosphor-icons/react';

/**
 * GEMINUS BOT — EMERALD SPECTRAL CLASS
 * SFUI-004 Rev 3 Bot Differentiation Standard
 * 
 * Geminus manages Federation storage and symmetry:
 * - Primary storage operations
 * - Mirror node synchronization
 * - Data integrity and symmetry enforcement
 * 
 * Spectral Treatment: EMERALD
 * - hue-rotate(120deg) saturate(1.3) brightness(1.05)
 * - Represents storage, mirroring, backup, integrity
 */

const GeminusBot = ({ 
  size = 80, 
  showLabel = true, 
  showStatus = false,
  animated = true,
  className = ''
}) => {
  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      {/* Geminus Bot Avatar */}
      <motion.div
        animate={animated ? {
          scaleX: [1, -1, 1],
          scale: [1, 1.02, 1]
        } : {}}
        transition={{
          duration: 6,
          repeat: animated ? Infinity : 0,
          ease: "easeInOut"
        }}
        className="relative"
        style={{ width: size, height: size }}
      >
        {/* Pulsing EMERALD Glow */}
        <motion.div
          animate={animated ? {
            scale: [1, 1.15, 1],
            opacity: [0.4, 0.7, 0.4]
          } : {}}
          transition={{
            duration: 2.5,
            repeat: animated ? Infinity : 0,
            ease: "easeInOut"
          }}
          className="absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,163,0.7) 0%, rgba(0,204,130,0.4) 50%, transparent 70%)',
            filter: 'blur(15px)'
          }}
        />
        
        {/* Geminus Face with EMERALD Spectral Filter */}
        <div 
          className="relative w-full h-full rounded-full overflow-hidden shadow-2xl border-2 border-[#00FFA3]/60"
          style={{
            boxShadow: '0 0 30px rgba(0, 255, 163, 0.7), 0 0 60px rgba(0, 204, 130, 0.5)'
          }}
        >
          <img 
            src="https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/65ca596591774b668101e8ec6924ac7b_IMG_3392.jpeg"
            alt="Geminus — Federation Mirror Node"
            className="w-full h-full object-cover"
            style={{
              filter: 'hue-rotate(120deg) saturate(1.3) brightness(1.05)'
            }}
          />
        </div>

        {/* Mirror/Copy Icon Overlay */}
        <motion.div
          animate={animated ? { 
            scale: [1, 1.2, 1],
            rotate: [0, 180, 0]
          } : {}}
          transition={{
            duration: 4,
            repeat: animated ? Infinity : 0,
            ease: "easeInOut"
          }}
          className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#00FFA3] rounded-full flex items-center justify-center border-2 border-[#030305] shadow-lg"
        >
          <ArrowsLeftRight size={16} weight="bold" className="text-white" />
        </motion.div>
      </motion.div>

      {/* Label */}
      {showLabel && (
        <div className="text-center">
          <p className="text-sm font-bold text-[#00FFA3] mb-0.5">
            Geminus
          </p>
          <p className="text-xs text-gray-400">
            Mirror Node
          </p>
        </div>
      )}

      {/* Status Indicator */}
      {showStatus && (
        <motion.div
          animate={{
            opacity: [0.6, 1, 0.6]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="flex items-center gap-2 px-3 py-1.5 bg-[#00FFA3]/20 border border-[#00FFA3]/50 rounded-full"
        >
          <div className="w-2 h-2 bg-[#00FFA3] rounded-full animate-pulse" />
          <span className="text-xs text-[#00FFA3] font-bold mono">
            MIRROR ACTIVE
          </span>
        </motion.div>
      )}
    </div>
  );
};

export default GeminusBot;
